import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useListsStore } from '@stores/lists'
import { useDisciplineBalancesStore } from '@stores/discipline-balances'

import { MAX_PER_PAGE, toPaginationValue } from '@helpers/route'
import { foldBracketArrayParams } from '@helpers/query-params'
import {
  DISCIPLINE_BALANCES_ARRAY_QUERY_KEYS,
  getCanonicalDisciplineBalancesQuery,
  queryToDisciplineBalancesFilters,
} from '@helpers/discipline-balances-query'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

/**
 * `/disciplines/{id}/balances` — query → store filters, as `middleware/surveys.ts` does. A query the
 * filters cannot show (app-main's `reason[0]=…`, an unknown reason) is rewritten once, so the URL, the
 * controls and the request always agree.
 */
export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalizedGeneric) => {
  const foldedQuery = foldBracketArrayParams(to.query, DISCIPLINE_BALANCES_ARRAY_QUERY_KEYS)
  const query = foldedQuery ?? to.query

  const lists = await useListsStore().ensure()
  const filters = queryToDisciplineBalancesFilters(query, lists?.balance ?? null)
  const canonicalQuery = getCanonicalDisciplineBalancesQuery(query, filters)

  if (foldedQuery || canonicalQuery) {
    return navigateTo({ path: to.path, query: canonicalQuery ?? query }, { replace: true })
  }

  const store = useDisciplineBalancesStore()

  store.setFilters(filters)
  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
  store.setModalOpen(false)
})
